// 地圖模式（plan/vms-web-v0.md §5.2.2 MAP-01 / 02 / 04、§5.2.3 FP-02）：
//   - 場域 / 家戶標記依健康上色（正常 / 有離線 / 警報中），縮小時聚合；點擊放大到該場域
//   - 戶外設備擺放：攝影機座標 + 朝向 + 視角扇形；感測器圖示與狀態
//   - 有對齊的平面圖疊在地圖上（2 點相似 / 3 點仿射），圖上的擺放一起投到地圖
//   - 供應商依地區自動（CN → 百度）、可手動切換並記在 vms.user_prefs；Google 3 秒載不起來改用百度
//   - 地址搜尋（MAP-05）：按下搜尋才問、點結果移過去；設定家戶位置時順便反查地址與國家（lib/geocode）
// 座標一律 WGS-84 存，顯示時轉（lib/geo）。
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KinioError } from '@kinio/web-sdk';
import { useApp } from '../../lib/AppContext';
import { defaultMapProvider, hasDevFeatures, type MapProvider } from '../../lib/appConfig';
import { applyAffine, sector, solveAffine, type Affine, type LatLng } from '../../lib/geo';
import { createMap, hasKey, pickProvider, type MapAdapter, type MapEngine, type MarkerSpec, type PolygonSpec, type ImageSpec } from '../../lib/maps';
import { useDevices } from '../../lib/useDevices';
import { useLiveEvents } from '../../lib/liveEvents';
import { isPresentMode } from '../../lib/present';
import { fullName, type VmsDevice } from '../../lib/devices';
import { vmsApi, type ApiPlacement } from '../../lib/planeApi';
import { LiveTile } from '../live/LiveTile';
import { useFloorplanImages, useHomeMapInfo, useOrgSites, usePrefs, type OwnedFloorplan } from './useMapData';
import { deviceGlyph, placementKey } from './placement';
import { geocodeApi, geocoderAvailable, type Place } from '../../lib/geocode';

/** IANA 時區清單（瀏覽器支援就用完整清單）。 */
const TIMEZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf('timeZone');
  } catch {
    return ['Asia/Taipei', 'Asia/Shanghai', 'Asia/Hong_Kong', 'Asia/Tokyo', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'UTC'];
  }
})();

const DEVICE_ZOOM = 16; // 這個級距以上才畫設備與平面圖
const DEFAULT_CENTER: LatLng = { lat: 25.0339, lng: 121.5645 };

type Tool = { kind: 'none' } | { kind: 'place'; key: string } | { kind: 'home'; homeId: string } | { kind: 'align'; fpId: string };

interface Pair {
  x: number;
  y: number;
  lat?: number;
  lng?: number;
}

interface Props {
  plans: OwnedFloorplan[];
  alerting: Map<string, number>;
  /** MAP-03：清單上點到的事件的設備（標記加框） */
  focusKey?: string;
  onOpenPlan(id: string): void;
  /** 底圖載入不了時〔改用平面圖〕 */
  onUsePlan?(): void;
}

export function GeoMapView({ plans, alerting, focusKey, onOpenPlan, onUsePlan }: Props) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { config } = useApp();
  const { tree, results } = useDevices();
  const { homes } = useHomeMapInfo();
  const { sensors } = useLiveEvents();
  const sites = useOrgSites();
  const api = results[0]?.home.endpoints.api;
  const prefs = usePrefs(api);
  const elRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapAdapter | null>(null);
  const [engine, setEngine] = useState<MapEngine | null>(null);
  const [tileFailed, setTileFailed] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [zoom, setZoom] = useState(12);
  const [tool, setTool] = useState<Tool>({ kind: 'none' });
  const [selHome, setSelHome] = useState<string | null>(null);
  const [tzEdit, setTzEdit] = useState<string | null>(null);
  const [selPl, setSelPl] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [pairs, setPairs] = useState<Pair[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [found, setFound] = useState<Place[] | null>(null);
  const [searching, setSearching] = useState(false);
  const canSearch = !!api && geocoderAvailable(config.env);
  const fitted = useRef(false);

  const devices = useMemo(() => tree.flatMap((d) => [d, ...d.children]), [tree]);
  const byKey = useMemo(() => new Map(devices.map((d) => [d.key, d])), [devices]);
  const siteById = useMemo(() => new Map(sites.map((s) => [s.site_id, s])), [sites]);
  const homeSite = useMemo(() => new Map(results.map((r) => [r.home.home_id, (r.home as { site_id?: string }).site_id])), [results]);

  // 家戶的點：自己的座標，沒有就用錨點場域的座標
  const homePoints = useMemo(
    () =>
      homes
        .map((h) => {
          const own = h.geo?.lat !== undefined && h.geo?.lng !== undefined ? { lat: h.geo.lat!, lng: h.geo.lng! } : null;
          const s = siteById.get(homeSite.get(h.homeId) ?? '');
          const fromSite = s?.geo?.lat !== undefined && s?.geo?.lng !== undefined ? { lat: s.geo.lat!, lng: s.geo.lng! } : null;
          return { h, pos: own ?? fromSite, country: h.geo?.country ?? s?.geo?.country };
        })
        .filter((x) => x.pos) as { h: (typeof homes)[number]; pos: LatLng; country?: string }[],
    [homes, siteById, homeSite],
  );

  // ---- 供應商 ----
  const country = homePoints[0]?.country ?? sites.find((s) => s.geo?.country)?.geo?.country;
  const preferred: MapProvider = defaultMapProvider(config, country, (prefs.data?.map_provider || null) as MapProvider | null);
  const want = prefs.isLoading ? null : pickProvider(config.maps, preferred, config.env);

  useEffect(() => {
    if (!want || !elRef.current) return;
    let alive = true;
    let m: MapAdapter | null = null;
    const center = homePoints[0]?.pos ?? DEFAULT_CENTER;
    (async () => {
      try {
        m = await createMap(want, elRef.current!, config.maps, center, 12, i18n.language);
      } catch (e) {
        // Google 載不起來 → 百度（有 key 的話），再不行 dev / test 用開發底圖
        const fb: MapEngine | null = want === 'google' && hasKey(config.maps, 'baidu') ? 'baidu' : hasDevFeatures(config.env) ? 'dev' : null;
        if (!fb || !alive) {
          setNotice(t('map.load_failed', { err: String((e as Error).message) }));
          return;
        }
        setNotice(t('map.fallback', { from: t(`map.provider_${want}`), to: t(`map.provider_${fb}`) }));
        m = await createMap(fb, elRef.current!, config.maps, center, 12, i18n.language);
      }
      if (!alive) return m?.destroy();
      mapRef.current = m;
      setEngine(m.provider);
      m.onZoom(setZoom);
      setTileFailed(false);
      m.onTileError?.(() => setTileFailed(true));
      setZoom(m.getZoom());
      fitted.current = false;
    })();
    return () => {
      alive = false;
      m?.destroy();
      mapRef.current = null;
      setEngine(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [want]);

  // 第一次有資料時框住所有點
  useEffect(() => {
    if (!engine || fitted.current || !homePoints.length) return;
    fitted.current = true;
    mapRef.current?.fit(homePoints.map((p) => p.pos));
  }, [engine, homePoints]);

  const images = useFloorplanImages(plans.filter((p) => p.geo_transform));

  // ---- 健康 ----
  function healthOf(homeId: string): 'alarm' | 'warn' | 'ok' {
    const ds = devices.filter((d) => d.homeId === homeId);
    if (ds.some((d) => alerting.has(d.key))) return 'alarm';
    if (ds.some((d) => !d.parentAid && d.presence === 'offline')) return 'warn';
    return 'ok';
  }

  // ---- 圖層 ----
  const layers = useMemo(() => {
    const markers: MarkerSpec[] = [];
    const polygons: PolygonSpec[] = [];
    const imgs: ImageSpec[] = [];
    const showDevices = zoom >= DEVICE_ZOOM;
    for (const { h, pos } of homePoints) {
      const hl = healthOf(h.homeId);
      if (showDevices && (h.placements.length || plans.some((p) => p.geo_transform && p.owner.kind === 'home' && p.owner.homeId === h.homeId))) continue; // 放大後由設備層接手
      markers.push({
        id: 'home:' + h.homeId,
        pos,
        glyph: homeSite.get(h.homeId) ? '🏢' : '🏠',
        label: h.label,
        cls: `site ${hl}`,
        title: `${h.label} · ${t(`map.health_${hl}`)}`,
        cluster: true,
        onClick: () => {
          setSelHome(h.homeId);
          const pts = [pos, ...h.placements.map((p) => ({ lat: p.lat!, lng: p.lng! }))];
          if (pts.length > 1) mapRef.current?.fit(pts);
          else mapRef.current?.setView(pos, 18);
        },
      });
    }
    // 沒有家戶掛著、但有座標的場域
    const used = new Set(homePoints.map((p) => homeSite.get(p.h.homeId)));
    for (const s of sites) {
      if (s.geo?.lat === undefined || s.geo?.lng === undefined || used.has(s.site_id) || s.parent_site_id) continue;
      markers.push({
        id: 'site:' + s.site_id,
        pos: { lat: s.geo.lat!, lng: s.geo.lng! },
        glyph: '🏢',
        label: s.name,
        cls: 'site idle',
        title: `${s.orgName} / ${s.name}`,
        cluster: true,
      });
    }
    if (showDevices) {
      for (const h of homes) {
        for (const p of h.placements) {
          if (p.lat === undefined || p.lng === undefined) continue;
          const key = placementKey(p);
          const d = byKey.get(key);
          const pos = { lat: p.lat, lng: p.lng };
          if (d?.isCamera)
            polygons.push({
              id: 'fov:' + key,
              path: sector(pos, p.heading_deg, p.fov_deg ?? 90, p.range_m ?? 20),
              cls: `fov ${alerting.has(key) ? 'alarm' : ''} ${selPl === key ? 'sel' : ''}`,
            });
          markers.push({
            id: 'dev:' + key,
            pos,
            glyph: deviceGlyph(d),
            label: d ? fullName(d, tree) : p.device_ref,
            cls: `device ${d?.presence ?? 'unknown'} ${alerting.has(key) ? 'alarm' : ''} ${selPl === key ? 'sel' : ''} ${focusKey === key ? 'focus' : ''} ${sensors.get(key)?.active ? 'sensor-on' : ''}`,
            onClick: () => {
              if (tool.kind === 'none' && editingHome(h.homeId)) setSelPl(key);
              else if (d?.isCamera) setOpen(key);
            },
          });
        }
      }
      for (const fp of plans) {
        const g = fp.geo_transform;
        if (!g) continue;
        const m = g.matrix as Affine;
        const url = images.get(fp.floorplan_id);
        const scale = 1000 / Math.max(fp.width, fp.height); // 圖的像素尺寸只影響 CSS 矩陣的單位
        if (url)
          imgs.push({
            id: fp.floorplan_id,
            url,
            width: fp.width * scale,
            height: fp.height * scale,
            tl: applyAffine(m, 0, 0),
            tr: applyAffine(m, 1, 0),
            bl: applyAffine(m, 0, 1),
            opacity: fp.opacity,
          });
        for (const p of fp.placements) {
          if (p.x === undefined || p.y === undefined) continue;
          const key = placementKey(p);
          const d = byKey.get(key);
          const pos = applyAffine(m, p.x, p.y);
          if (d?.isCamera) {
            // 平面圖上 0° = 圖的上方；換成地圖方位要加上圖的旋轉
            const up = applyAffine(m, p.x, p.y - 0.01);
            const bearing = (Math.atan2((up.lng - pos.lng) * Math.cos((pos.lat * Math.PI) / 180), up.lat - pos.lat) * 180) / Math.PI;
            polygons.push({
              id: `fpfov:${fp.floorplan_id}:${key}`,
              path: sector(pos, bearing + p.heading_deg, p.fov_deg ?? 90, p.range_m ?? 8),
              cls: `fov indoor ${alerting.has(key) ? 'alarm' : ''}`,
            });
          }
          markers.push({
            id: `fpdev:${fp.floorplan_id}:${key}`,
            pos,
            glyph: deviceGlyph(d),
            cls: `device indoor ${d?.presence ?? 'unknown'} ${alerting.has(key) ? 'alarm' : ''} ${focusKey === key ? 'focus' : ''} ${sensors.get(key)?.active ? 'sensor-on' : ''}`,
            title: d ? fullName(d, tree) : key,
            onClick: () => (d?.isCamera ? setOpen(key) : onOpenPlan(fp.floorplan_id)),
          });
        }
      }
    }
    // 對齊中：已點的地圖控制點
    if (tool.kind === 'align')
      pairs.forEach((p, i) => p.lat !== undefined && markers.push({ id: 'cp:' + i, pos: { lat: p.lat, lng: p.lng! }, glyph: String(i + 1), cls: 'ctrl' }));
    return { markers, polygons, images: imgs };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homePoints, homes, sites, plans, images, byKey, alerting, zoom, tool, pairs, selPl, tree, sensors, focusKey]);

  useEffect(() => {
    mapRef.current?.setLayers(layers);
  }, [layers, engine]);

  // ---- 地址搜尋（MAP-05）----
  async function search() {
    const text = q.trim();
    if (!text || !api) return;
    setSearching(true);
    setErr(null);
    try {
      setFound(await geocodeApi.search(api, text, i18n.language));
    } catch (e) {
      setFound(null);
      setErr(e instanceof KinioError && e.status === 429 ? t('map.search_busy') : String((e as Error).message ?? e));
    } finally {
      setSearching(false);
    }
  }

  // ---- 編輯 ----
  const selHomeInfo = homes.find((h) => h.homeId === selHome) ?? (homes.length === 1 ? homes[0] : undefined);
  function editingHome(homeId: string) {
    return !!homes.find((h) => h.homeId === homeId)?.canWrite;
  }
  const refreshPl = () => qc.invalidateQueries({ queryKey: ['geo-placements'] });

  async function putPlacement(homeId: string, api: string, p: ApiPlacement & { lat: number; lng: number }) {
    setErr(null);
    try {
      await vmsApi.putGeoPlacement(api, homeId, p.device_ref, {
        expected_version: p.version ?? 0,
        lat: p.lat,
        lng: p.lng,
        heading_deg: ((p.heading_deg % 360) + 360) % 360,
        fov_deg: p.fov_deg,
        range_m: p.range_m,
      });
    } catch (e) {
      setErr(e instanceof KinioError && e.status === 409 ? t('map.conflict') : String((e as Error).message ?? e));
    }
    await refreshPl();
  }

  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    m.onClick(async (pos) => {
      if (tool.kind === 'place') {
        const d = byKey.get(tool.key);
        const h = homes.find((x) => x.homeId === d?.homeId);
        if (!d || !h) return;
        const cur = h.placements.find((p) => placementKey(p) === tool.key);
        await putPlacement(h.homeId, h.api, {
          ...(cur ?? { home_id: h.homeId, device_ref: d.aid, heading_deg: 0, fov_deg: d.isCamera ? 90 : undefined, range_m: d.isCamera ? 20 : undefined }),
          lat: pos.lat,
          lng: pos.lng,
        });
        setSelPl(tool.key);
        setTool({ kind: 'none' });
      } else if (tool.kind === 'home') {
        const h = homes.find((x) => x.homeId === tool.homeId);
        if (!h) return;
        try {
          // 反查地址與國家（國家決定地圖供應商）；查不到不擋儲存
          const addr = canSearch ? await geocodeApi.reverse(h.api, pos, i18n.language).catch(() => null) : null;
          await vmsApi.setHomeGeo(h.api, h.homeId, {
            ...(h.geo ?? {}),
            lat: pos.lat,
            lng: pos.lng,
            ...(addr ? { address: addr.display_name, ...(addr.country_code ? { country: addr.country_code } : {}) } : {}),
          });
        } catch (e) {
          setErr(String((e as Error).message ?? e));
        }
        await qc.invalidateQueries({ queryKey: ['home-geo'] });
        setTool({ kind: 'none' });
      } else if (tool.kind === 'align') {
        setPairs((l) => {
          const i = l.findIndex((p) => p.lat === undefined);
          if (i < 0) return l;
          const next = [...l];
          next[i] = { ...next[i], lat: pos.lat, lng: pos.lng };
          return next;
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, engine, homes, byKey]);

  const sel = selPl ? homes.flatMap((h) => h.placements.map((p) => ({ h, p }))).find((x) => placementKey(x.p) === selPl) : undefined;
  const alignFp = tool.kind === 'align' ? plans.find((p) => p.floorplan_id === tool.fpId) : undefined;
  const alignImg = useFloorplanImages(alignFp ? [alignFp] : []).get(alignFp?.floorplan_id ?? '');

  async function saveAlign() {
    if (!alignFp) return;
    const pts = pairs.filter((p) => p.lat !== undefined) as Required<Pair>[];
    const m = solveAffine(pts, alignFp.width / alignFp.height);
    if (!m) return setErr(t('map.align_bad'));
    try {
      await vmsApi.updateFloorplan(alignFp.owner, alignFp, { geo_transform: { points: pts, matrix: m } });
      setTool({ kind: 'none' });
      setPairs([]);
    } catch (e) {
      setErr(e instanceof KinioError && e.status === 409 ? t('map.conflict') : String((e as Error).message ?? e));
    }
    await qc.invalidateQueries({ queryKey: ['floorplans'] });
  }

  async function setOpacity(fp: OwnedFloorplan, v: number) {
    try {
      await vmsApi.updateFloorplan(fp.owner, fp, { opacity: v });
    } catch {
      /* 版本衝突就等下次讀取 */
    }
    await qc.invalidateQueries({ queryKey: ['floorplans'] });
  }

  async function switchProvider(p: MapProvider) {
    if (!api) return;
    await vmsApi.setPrefs(api, { ...(prefs.data ?? {}), map_provider: p }).catch(() => {});
    await qc.invalidateQueries({ queryKey: ['vms-prefs'] });
  }

  const openDev = open ? byKey.get(open) : undefined;
  const placeableHome = selHomeInfo?.canWrite ? selHomeInfo : undefined;
  const alignable = plans.filter((p) => p.canWrite);

  return (
    <div className="map-body">
      <aside className="cam-list">
        {/* 供應商切換只列有金鑰的；只剩一家（或都沒有、用開發底圖）就不顯示切換鈕（VMS UX 評審第三輪 R3-P1-6） */}
        {(['google', 'baidu'] as MapProvider[]).filter((p) => hasKey(config.maps, p)).length > 1 && (
          <>
            <h2>{t('map.provider')}</h2>
            <div className="seg" role="group" aria-label={t('map.provider')}>
              {(['google', 'baidu'] as MapProvider[]).map((p) => (
                <button key={p} aria-pressed={engine === p} onClick={() => void switchProvider(p)}>
                  {t(`map.provider_${p}`)}
                </button>
              ))}
            </div>
          </>
        )}
        {engine === 'dev' && !isPresentMode() && <p className="muted small">{t('map.dev_basemap')}</p>}

        <h2>{t('map.sites')}</h2>
        {homes.map((h) => {
          const pt = homePoints.find((x) => x.h.homeId === h.homeId);
          const hl = healthOf(h.homeId);
          return (
            <div key={h.homeId} className={`cam-item map-site-row ${selHomeInfo?.homeId === h.homeId ? 'on-wall' : ''}`}>
              <button
                className="link"
                onClick={() => {
                  setSelHome(h.homeId);
                  if (pt) mapRef.current?.setView(pt.pos, 18);
                }}
              >
                {/* 狀態點的意思寫在滑過提示（第四輪 R4-P2-4） */}
                <span className={`dot health-${hl}`} title={t('map.site_health_' + hl)} aria-label={t('map.site_health_' + hl)} role="img" />
                {h.label}
              </button>
              {/* 第二行固定格式「位置：未設定〔📍 設定〕 · 時區：Asia/Taipei〔改〕」（第五輪 B5-12：不再擠成一行亂換行） */}
              <div className="map-site-meta muted small" data-testid="map-site-meta">
              <span>{pt ? t('map.location_set') : t('map.location_unset')}</span>
              {h.canWrite && (
                <button className="link-btn small" title={t('map.set_location')} onClick={() => setTool({ kind: 'home', homeId: h.homeId })} data-testid="map-set-location">
                  📍 {pt ? t('map.location_move') : t('map.location_pick')}
                </button>
              )}
              {/* 瀏覽地圖時只顯示時區（第四輪 B4-12）；要改按〔改〕才出現選單，不會滑過就改到場域設定 */}
              {(!h.canWrite || tzEdit !== h.homeId) && (
                <span className="tz-text" data-testid="map-tz">
                  {' · '}
                  {t('map.timezone_label', { tz: h.geo?.timezone || t('map.timezone_unset_short') })}
                  {/* 「Asia/Taipei 改」中間留空格（第六輪 R6-P2-5） */}
                  {h.canWrite && ' '}
                  {h.canWrite && (
                    <button type="button" className="link-btn small" onClick={() => setTzEdit(h.homeId)} aria-label={t('map.timezone_edit')}>
                      {t('map.timezone_change')}
                    </button>
                  )}
                </span>
              )}
              {h.canWrite && tzEdit === h.homeId && (
                <select
                  autoFocus
                  onBlur={() => setTzEdit(null)}
                  className="input tz-select"
                  aria-label={t('map.timezone')}
                  value={h.geo?.timezone ?? ''}
                  onChange={async (e) => {
                    setTzEdit(null);
                    await vmsApi.setHomeGeo(h.api, h.homeId, { ...(h.geo ?? {}), timezone: e.target.value }).catch((err) => setErr(String(err.message ?? err)));
                    await qc.invalidateQueries({ queryKey: ['home-geo'] });
                    await qc.invalidateQueries({ queryKey: ['me-devices'] });
                  }}
                >
                  <option value="">{t('map.timezone_unset')}</option>
                  {TIMEZONES.map((z) => (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ))}
                </select>
              )}
              </div>
            </div>
          );
        })}

        {placeableHome &&
          (() => {
            // 「戶外擺放」改成「還沒放到地圖上的設備（N）」，放過的收在下面（第三輪 R3-P1-6）
            const list = devices.filter((d) => d.homeId === placeableHome.homeId && (d.isCamera || d.children.length === 0));
            const isPlaced = (d: VmsDevice) => placeableHome.placements.some((p) => placementKey(p) === d.key);
            const row = (d: VmsDevice) => (
              <button
                key={d.key}
                className={`cam-item ${tool.kind === 'place' && tool.key === d.key ? 'on-wall' : ''}`}
                onClick={() => setTool({ kind: 'place', key: d.key })}
                title={t('map.click_map_to_place')}
              >
                <span className={`dot ${d.presence}`} />
                {fullName(d, tree)}
                {isPlaced(d) && <span className="muted small"> ✓</span>}
              </button>
            );
            const todo = list.filter((d) => !isPlaced(d));
            const done = list.filter(isPlaced);
            return (
              <>
                {/* 數字含 NVR / Station 的通道時寫明（第六輪 R6-P2-5：「16」跟範圍的「13 台」對不上） */}
                <h2 data-testid="map-unplaced-title">
                  {todo.some((d) => d.parentAid)
                    ? t('map.unplaced_devices_ch', { home: placeableHome.label, count: todo.length, ch: todo.filter((d) => d.parentAid).length })
                    : t('map.unplaced_devices', { home: placeableHome.label, count: todo.length })}
                </h2>
                {todo.length ? todo.map(row) : <p className="muted small">{t('map.all_placed')}</p>}
                {done.length > 0 && (
                  <details className="small">
                    <summary>{t('map.placed_devices', { count: done.length })}</summary>
                    {done.map(row)}
                  </details>
                )}
              </>
            );
          })()}

        {alignable.length > 0 && (
          <>
            <h2>{t('map.overlays')}</h2>
            {alignable.map((fp) => (
              <div key={fp.floorplan_id} className="overlay-row">
                <span>
                  🗺 {fp.name} {fp.geo_transform ? '✓' : ''}
                </span>
                <button className="btn btn-sm" onClick={() => (setTool({ kind: 'align', fpId: fp.floorplan_id }), setPairs([]))}>
                  {t('map.align')}
                </button>
                {fp.geo_transform && (
                  <input
                    type="range"
                    min={0.1}
                    max={1}
                    step={0.05}
                    defaultValue={fp.opacity}
                    aria-label={t('map.opacity')}
                    onChange={(e) => void setOpacity(fp, Number(e.target.value))}
                  />
                )}
              </div>
            ))}
          </>
        )}
      </aside>
      <div className="wall-area">
        {canSearch && engine && (
          <form
            className="toolbar geo-search"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              void search();
            }}
          >
            <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('map.search_placeholder')} aria-label={t('map.search_placeholder')} maxLength={200} />
            <button className="btn btn-sm" disabled={searching || !q.trim()}>
              {t('map.search')}
            </button>
            {found && (
              <ul className="geo-results" data-testid="geo-results">
                {found.length === 0 && <li className="muted small">{t('map.search_none')}</li>}
                {found.map((p, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => {
                        mapRef.current?.setView({ lat: p.lat, lng: p.lng }, 17);
                        setFound(null);
                      }}
                    >
                      {p.display_name}
                    </button>
                  </li>
                ))}
                <li className="muted small">{t('map.search_attrib')}</li>
              </ul>
            )}
          </form>
        )}
        {(notice || err) && <p className={err ? 'error' : 'muted small notice'}>{err ?? notice}</p>}
        {tool.kind === 'place' && <p className="pb-notice">{t('map.click_map_to_place')}</p>}
        {tool.kind === 'home' && <p className="pb-notice">{t('map.click_map_home')}</p>}
        {!want && !prefs.isLoading && <div className="card planned">{t('map.no_map_key')}</div>}
        {tileFailed && (
          <p className="notice small row" data-testid="map-tiles-failed">
            {t('map.tiles_failed')}
            {onUsePlan && (
              <button type="button" className="btn btn-sm" onClick={onUsePlan}>
                {t('map.use_plan')}
              </button>
            )}
          </p>
        )}
        <div className="geo-wrap">
          <div ref={elRef} className="geo-map" data-engine={engine ?? ''} />
          {sel && editingHome(sel.h.homeId) && tool.kind === 'none' && (
            <div className="geo-edit card">
              <strong>{byKey.get(selPl!) ? fullName(byKey.get(selPl!)!, tree) : selPl}</strong>
              <label>
                {t('map.heading')} {Math.round(sel.p.heading_deg)}°
                <input
                  type="range"
                  min={0}
                  max={359}
                  defaultValue={sel.p.heading_deg}
                  key={`h${sel.p.version}`}
                  onMouseUp={(e) =>
                    void putPlacement(sel.h.homeId, sel.h.api, { ...sel.p, heading_deg: Number(e.currentTarget.value), lat: sel.p.lat!, lng: sel.p.lng! })
                  }
                />
              </label>
              {byKey.get(selPl!)?.isCamera && (
                <>
                  <label>
                    {t('map.fov')} {sel.p.fov_deg ?? 90}°
                    <input
                      type="range"
                      min={20}
                      max={360}
                      defaultValue={sel.p.fov_deg ?? 90}
                      key={`f${sel.p.version}`}
                      onMouseUp={(e) =>
                        void putPlacement(sel.h.homeId, sel.h.api, { ...sel.p, fov_deg: Number(e.currentTarget.value), lat: sel.p.lat!, lng: sel.p.lng! })
                      }
                    />
                  </label>
                  <label>
                    {t('map.range')} {sel.p.range_m ?? 20} m
                    <input
                      type="range"
                      min={2}
                      max={200}
                      defaultValue={sel.p.range_m ?? 20}
                      key={`r${sel.p.version}`}
                      onMouseUp={(e) =>
                        void putPlacement(sel.h.homeId, sel.h.api, { ...sel.p, range_m: Number(e.currentTarget.value), lat: sel.p.lat!, lng: sel.p.lng! })
                      }
                    />
                  </label>
                </>
              )}
              <div className="toolbar">
                <button className="btn btn-sm" onClick={() => setTool({ kind: 'place', key: selPl! })}>
                  {t('map.move')}
                </button>
                <button
                  className="btn btn-sm"
                  onClick={async () => {
                    await vmsApi.deleteGeoPlacement(sel.h.api, sel.h.homeId, sel.p.device_ref, sel.p.version ?? 0).catch((e) => setErr(String(e.message ?? e)));
                    setSelPl(null);
                    await refreshPl();
                  }}
                >
                  {t('map.remove')}
                </button>
                <button className="btn btn-sm" onClick={() => setSelPl(null)}>
                  {t('common.close')}
                </button>
              </div>
            </div>
          )}
          {alignFp && (
            <div className="geo-align card">
              <strong>{t('map.align_title', { name: alignFp.name })}</strong>
              <p className="muted small">{t('map.align_help')}</p>
              <div
                className="align-img"
                style={{ aspectRatio: `${alignFp.width} / ${alignFp.height}` }}
                onClick={(e) => {
                  if (pairs.length >= 3 || pairs.some((p) => p.lat === undefined)) return;
                  const r = e.currentTarget.getBoundingClientRect();
                  setPairs((l) => [...l, { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height }]);
                }}
              >
                {alignImg && <img src={alignImg} alt="" draggable={false} />}
                {pairs.map((p, i) => (
                  <span key={i} className={`cp ${p.lat === undefined ? 'pending' : ''}`} style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}>
                    {i + 1}
                  </span>
                ))}
              </div>
              <p className="small">
                {pairs.some((p) => p.lat === undefined)
                  ? t('map.align_click_map', { n: pairs.length })
                  : pairs.length >= 3
                    ? t('map.align_ready')
                    : t('map.align_click_img', { n: pairs.length + 1 })}
              </p>
              <div className="toolbar">
                <button className="btn btn-primary btn-sm" disabled={pairs.filter((p) => p.lat !== undefined).length < 2} onClick={() => void saveAlign()}>
                  {t('map.align_save', { n: pairs.filter((p) => p.lat !== undefined).length })}
                </button>
                <button className="btn btn-sm" onClick={() => setPairs([])}>
                  {t('map.align_reset')}
                </button>
                {alignFp.geo_transform && (
                  <button
                    className="btn btn-sm"
                    onClick={async () => {
                      await vmsApi.updateFloorplan(alignFp.owner, alignFp, { geo_transform: null }).catch(() => {});
                      await qc.invalidateQueries({ queryKey: ['floorplans'] });
                      setTool({ kind: 'none' });
                    }}
                  >
                    {t('map.align_clear')}
                  </button>
                )}
                <button className="btn btn-sm" onClick={() => (setTool({ kind: 'none' }), setPairs([]))}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
          {openDev && (
            <div className="fp-popup">
              <LiveTile cam={openDev as VmsDevice} label={fullName(openDev, tree)} onClose={() => setOpen(null)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
